import { useEffect, useRef, useState } from "react";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.css";
import { systemPrompt } from "./chatComponents/prompt";
import { sendMessage } from "./utility/sendMessage";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Box, Button, Dialog, Divider, Typography } from "@mui/material";
import { putCapitalizationData } from "./utility/putCapitalizationData";
import { putConversationData } from "./utility/putConversationData";
import { useLocation, useNavigate } from "react-router-dom";
import { getConversationData } from "./utility/getConversationData";
import { getDateForView } from "./utility/getFormattedDate";
import { generateRandomString } from "./utility/generateRandomString";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { ChatCompleteConfirmDialog } from "./chatComponents/ChatCompleteConfirmDialog";
import { MessageViewContainer } from "./chatComponents/MessageViewContainer";
import { MessageInputContainer } from "./chatComponents/MessageInputContainer";
import { AnswerCheckDialog } from "./chatComponents/AnswerCheckDialog";
import { baseWhite } from "./utility/colorPalette";
import { useAuth } from "../context/AuthContext";
import { assistantWithCreateThread } from "./utility/assistantWithCreateThread";
import { getOpenAIAssistantConversation } from "./utility/getOpenAIAssistantConversation";
import { assistantContinueConversation } from "./utility/assistantContinueConversation";
import { PreparationComponent } from "./chatComponents/PreparationComponent";

interface messageData {
  content: string;
  role: string;
}

const firstMessage: messageData = {
  role: "system",
  content: systemPrompt,
};

export const ChatPage = () => {
  const navigate = useNavigate();
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const hasRun = useRef(false);
  const { user } = useAuthenticator((context) => [context.user]);
  const { idToken, refreshAuthSession } = useAuth();
  const [conversationData, setConversationData] = useState<messageData[]>([]);
  const location = useLocation();
  const state = location.state as {
    conversationId?: string;
    complete?: boolean;
    conversationDate?: string;
    threadId?: string;
  };
  const [threadId, setThreadId] = useState<string>("");
  // 新規に会話を作成した場合はstateで管理する
  const [conversationId, setConversationId] = useState<string>("");

  const [chatCompleteDialogIsOn, setChatCompleteDialogIsOn] =
    useState<boolean>(false);

  const [answerCheckDialogIsOn, setAnswerCheckDialogIsOn] =
    useState<boolean>(false);

  const [preparationComplete, setPreparationComplete] =
    useState<boolean>(false);

  useEffect(() => {
    console.log(preparationComplete);
    if (hasRun.current) return; // ２回目以降は何もしない
    hasRun.current = true;
    console.log(state.conversationId);

    // 会話IDがある時はデータを読み込む
    if (state.conversationId && idToken) {
      if (state.threadId) {
        // メタデータにスレッドが存在する場合はOPENAIから読み込む
        getOpenAIAssistantConversation(state.threadId, idToken).then((data) => {
          // 最初の「こんにちは」を除くためにスライスする
          setConversationData(data.messages.slice(1));
          setPreparationComplete(true);
        });
        setThreadId(state.threadId);
      } else {
        // スレッドIDが無い場合（アシスタントに切り替える前のデータ）はDBから読み込む
        getConversationData(state.conversationId, idToken).then((data) => {
          console.log(data);
          if (data.messages.length > 0) {
            setConversationData(data.messages);
          }
          setPreparationComplete(true);
        });
      }
    }

    //会話データが無いときは新規の会話を作成しDBに保存
    else {
      const randomString = generateRandomString(8);
      const tmpConversationId = `${user.userId}-${state.conversationDate}-${randomString}`;

      // メタデータ保存処理
      if (state.conversationDate && idToken) {
        // スレッド作成
        assistantWithCreateThread(idToken).then((data) => {
          setThreadId(data.threadId);

          // 受け取った最初のメッセージをstateとDBに保存
          const receivedMessage: messageData = {
            role: "assistant",
            content: data.messages[0].content[0].text.value,
          };
          setConversationData((prevMessage) => [
            ...prevMessage,
            receivedMessage,
          ]);
          putConversationData(tmpConversationId, [receivedMessage], idToken);

          // スレッドIDを含めてメタデータを保存
          const capitalizationData = {
            userId: user.userId,
            conversationId: tmpConversationId,
            date: state.conversationDate!,
            complete: false,
            threadId: data.threadId,
          };
          putCapitalizationData(capitalizationData, idToken);
          setPreparationComplete(true);
        });

        setConversationId(tmpConversationId);
      } else {
        // エラー処理をしておく。直リンクでチャットページに来た場合。
      }
    }
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationData]);

  // メッセージ送信処理実行
  const sendAndReceiveMessage = (input: string) => {
    const newMessage = {
      role: "user",
      content: input,
    };

    const addedNewMessage = [...conversationData, newMessage];
    setConversationData((prevMessage) => [...prevMessage, newMessage]);
    // 会話データ更新の度に画面を最下部にスクロール
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });

    if (!idToken) return;
    if (!threadId) return;

    assistantContinueConversation(idToken, input, threadId).then((data) => {
      const receivedMessage: messageData = {
        role: "assistant",
        content: data.allMessages[0].content[0].text.value,
      };
      setConversationData((prevMessage) => [...prevMessage, receivedMessage]);
      putConversationData(
        conversationId,
        [...addedNewMessage, receivedMessage],
        idToken
      );
    });
  };

  if (!preparationComplete) return <PreparationComponent />;

  return (
    <Box
      sx={{
        // height: "calc(100dvh - 56px)", // iPhone対応の高さ
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          textAlign: "center",
          backgroundColor: baseWhite,
          pt: 0.5,
          flexShrink: 0, // ヘッダーのサイズを固定
        }}
      >
        {state.conversationDate && (
          <Typography variant="body2">
            {getDateForView(state.conversationDate)}の会話
          </Typography>
        )}
        {!state.complete && (
          <Box sx={{ textAlign: "center", padding: "0.5rem" }}>
            <Button
              variant="contained"
              // color="secondary"
              onClick={() => setChatCompleteDialogIsOn(true)}
            >
              会話を終了する
            </Button>
          </Box>
        )}
        {state.complete && (
          <Button onClick={() => setAnswerCheckDialogIsOn(true)}>
            アンケート参照
          </Button>
        )}
        <Divider />
      </Box>

      {/* 戻るボタン */}
      {/* <Box sx={{ flexShrink: 0 }}>
        <Button onClick={() => navigate("/")}>
          <ArrowBackIosIcon />
          戻る
        </Button>
      </Box> */}

      {/* メッセージ表示領域 */}

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0, // flexアイテムの最小高さを0に
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end", // 下端に配置
            WebkitOverflowScrolling: "touch",
            padding: state.complete ? "8px" : "0px",
          }}
        >
          <MessageViewContainer
            conversationData={conversationData}
            bottomRef={bottomRef}
          />
        </Box>
      </Box>

      {/* <PreparationComponent /> */}

      {/* メッセージ入力部 */}
      {!state.complete && (
        <Box
          sx={{
            flexShrink: 0,
            background: "linear-gradient(to bottom right, #e0f8e9, #c6f0d5)",
            borderTop: "1px solid #e0e0e0",
            position: "sticky", // stickyで最下部に固定
            bottom: 0,
            zIndex: 5,
          }}
        >
          <MessageInputContainer
            sendAndReceiveMessage={sendAndReceiveMessage}
          />
        </Box>
      )}

      <ChatCompleteConfirmDialog
        isOpen={chatCompleteDialogIsOn}
        setIsOpen={setChatCompleteDialogIsOn}
        conversationDate={state.conversationDate}
      />

      <AnswerCheckDialog
        isOn={answerCheckDialogIsOn}
        setIsOn={setAnswerCheckDialogIsOn}
        date={state.conversationDate}
      />
    </Box>
  );
};
